import React, {Component} from 'react';
import PropTypes from 'prop-types';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import {WebMercatorViewport} from '@deck.gl/core';

import {Transition, TextInput, Group, Paper, Stack, UnstyledButton, Text} from '@mantine/core';
import {IconSearch} from '@tabler/icons-react';
import Localization from '../../Localization';

class Geocoder extends Component {
  debounceTimeout = null;
  isMounted_ = true;
  latestQuery = null;
  state = {
    results: [],
    showResults: false,
    inputValue: ''
  };

  onChange = (event) => {
    const {timeout, queryParams, localGeocoder, limit, localOnly} = this.props;
    const queryString = event.target.value;

    this.setState({
      inputValue: event.target.value
    });

    clearTimeout(this.debounceTimeout);
    this.latestQuery = queryString;
    this.debounceTimeout = setTimeout(() => {
      const localResults = localGeocoder ? localGeocoder(queryString) : [];
      const params = {...queryParams, ...{limit: limit - localResults.length}};

      if (params.limit > 0 && !localOnly && queryString.length > 0) {
        this.geocodingClient.forwardGeocode({
          query: queryString,
          limit: params.limit,
          ...params
        }).send().then((res) => {
          if (!this.isMounted_ || this.latestQuery !== queryString) {
            return;
          }
          this.setState({
            results: [...localResults, ...(res.body.features || [])]
          });
        }).catch(() => {});
      } else {
      this.setState({
          results: localResults
      });
      }
    }, timeout);
  };
  onSelected = (item) => {
    const {viewport, onSelected, transitionDuration, hideOnSelect, pointZoom, formatItem, updateInputOnSelect} = this.props;
    let newViewport = new WebMercatorViewport(viewport);
    const {bbox, center} = item;

    if (bbox) {
        newViewport = newViewport.fitBounds([
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]]
        ]);
    } else {
        newViewport = {
            longitude: center[0],
            latitude: center[1],
            zoom: pointZoom
        };
    }

    const {longitude, latitude, zoom} = newViewport;

    onSelected({...viewport, ...{longitude, latitude, zoom, transitionDuration}}, item);

    if (hideOnSelect) {
        // this.setState({results: []});
    }

    if (updateInputOnSelect) {
        this.setState({inputValue: formatItem(item)});
    }
  };

  showResults = () => {
      this.setState({showResults: true});
  };

  hideResults = () => {
      setTimeout(() => {
          this.setState({showResults: false});
      }, 300);
  };

  constructor(props) {
      super();

      this.geocodingClient = mbxGeocoding({ accessToken: props.mapboxApiAccessToken });

      if (props.initialInputValue !== '') {
          this.state.inputValue = props.initialInputValue;
      }
  }

  componentWillUnmount() {
      this.isMounted_ = false;
      clearTimeout(this.debounceTimeout);
  }

  render() {
      const {results, showResults, inputValue} = this.state;
      const {formatItem, className, inputComponent, itemComponent} = this.props;

      const Input = inputComponent || 'input';
      const Item = itemComponent || 'div';

      return (
          <div style={{position:"relative"}}>
              <Group gap="xs" align="flex-end">
                <TextInput id="input-with-icon-grid" label={Localization.strings.KEYWORD}
                  size="sm"
                  leftSection={<IconSearch size={18}/>}
                  onChange={this.onChange} onBlur={this.hideResults} onFocus={this.showResults} value={inputValue}
                />
              </Group>

              <Transition mounted={showResults && !!results.length} transition="fade" duration={200}>
                {(styles) => (
                  <Paper style={{...styles, position:"absolute", zIndex:1, display: "flex", backgroundColor:"white"}} shadow="sm">
                    <Stack gap={0} aria-label="mailbox folders">
                      {results.map((item, index) => (
                          <UnstyledButton key={index} onClick={() => this.onSelected(item)} px="sm" py={4}>
                            <Text size="sm">{formatItem(item)}</Text>
                          </UnstyledButton>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Transition>
          </div>
      );
  }
}

Geocoder.propTypes = {
    timeout: PropTypes.number,
    queryParams: PropTypes.object,
    viewport: PropTypes.object.isRequired,
    onSelected: PropTypes.func.isRequired,
    transitionDuration: PropTypes.number,
    hideOnSelect: PropTypes.bool,
    pointZoom: PropTypes.number,
    mapboxApiAccessToken: PropTypes.string.isRequired,
    formatItem: PropTypes.func,
    className: PropTypes.string,
    inputComponent: PropTypes.func,
    itemComponent: PropTypes.func,
    limit: PropTypes.number,
    localGeocoder: PropTypes.func,
    localOnly: PropTypes.bool,
    updateInputOnSelect: PropTypes.bool,
    initialInputValue: PropTypes.string
};

Geocoder.defaultProps = {
    timeout: 300,
    transitionDuration: 0,
    hideOnSelect: false,
    updateInputOnSelect: false,
    pointZoom: 16,
    formatItem: item => item.place_name,
    queryParams: {},
    className: '',
    limit: 10,
    initialInputValue: ''
};

export default Geocoder;